import { useQueryClient } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { CheckCircle, Clock, Mail, XCircle } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { Box } from '../src/components/primitives/Box';
import { Button } from '../src/components/primitives/Button';
import { VStack } from '../src/components/primitives/Stack';
import { Text } from '../src/components/primitives/Text';
import { useAuth } from '../src/contexts/AuthContext';
import { useTranslation } from '../src/contexts/TranslationContext';
import api from '../src/lib/api';
import { colors } from '../src/theme/colors';
import { tokens } from '../src/theme/tokens';

type InviteStatus =
  | 'loading'
  | 'valid'
  | 'expired'
  | 'invalid'
  | 'accepted'
  | 'error'
  | 'accepting';

interface InviteInfo {
  organizationName: string;
  email: string;
  role: string;
  expiresAt: string;
}

export default function InvitePage() {
  const { token } = useLocalSearchParams<{ token: string }>();
  const router = useRouter();
  const { isAuthenticated, backendUser, setCurrentOrganization, refreshUser } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<InviteStatus>('loading');
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    validateToken();
  }, [token]);

  async function validateToken() {
    try {
      const { data } = await api.get(`/auth/invite/${token}`);
      if (data.valid) {
        setInviteInfo({
          organizationName: data.organization?.name ?? 'the team',
          email: data.email ?? '',
          role: data.role ?? 'member',
          expiresAt: data.expiresAt ?? '',
        });
        setStatus('valid');
      } else {
        setStatus(data.reason === 'expired' ? 'expired' : 'invalid');
      }
    } catch {
      setStatus('invalid');
    }
  }

  async function handleAccept() {
    if (!isAuthenticated) {
      router.replace(`/login?invite=${encodeURIComponent(token)}`);
      return;
    }

    setStatus('accepting');
    try {
      const { data } = await api.post(`/organizations/invites/${token}/accept`);

      await refreshUser();

      const orgName = data.organization?.name ?? inviteInfo?.organizationName ?? 'Organization';
      const orgCreatedAt = data.organization?.createdAt ?? new Date().toISOString();

      setCurrentOrganization({
        id: data.organizationId,
        name: orgName,
        createdAt: orgCreatedAt,
      });

      queryClient.invalidateQueries();

      setStatus('accepted');
      setTimeout(() => router.replace('/dashboard'), 2000);
    } catch (error: any) {
      const httpStatus = error?.response?.status;

      if (httpStatus === 409) {
        await refreshUser();
        queryClient.invalidateQueries();
        setStatus('accepted');
        setTimeout(() => router.replace('/dashboard'), 2000);
        return;
      }

      setErrorMessage(error?.response?.data?.message || 'Failed to accept invite');
      setStatus('error');
    }
  }

  function handleLoginAndAccept() {
    router.replace(`/login?invite=${encodeURIComponent(token)}`);
  }

  return (
    <View style={styles.container}>
      <Box
        backgroundColor={colors.background.card}
        borderRadius="xl"
        borderWidth={1}
        borderColor={colors.border.muted}
        padding="xl"
        style={styles.card}
      >
        {status === 'loading' && (
          <VStack spacing="lg" style={styles.center}>
            <ActivityIndicator size="large" color={colors.primary[500]} />
            <Text variant="body" color={colors.text.secondary}>
              Validating invitation...
            </Text>
          </VStack>
        )}

        {status === 'valid' && inviteInfo && (
          <VStack spacing="lg" style={styles.center}>
            <Mail size={48} color={colors.primary[500]} />
            <Text variant="h5" color={colors.text.primary} style={styles.textCenter}>
              You're invited to join
            </Text>
            <Text variant="h4" color={colors.primary[500]} style={styles.textCenter}>
              {inviteInfo.organizationName}
            </Text>
            <Text variant="body" color={colors.text.secondary} style={styles.textCenter}>
              Role: {inviteInfo.role.charAt(0).toUpperCase() + inviteInfo.role.slice(1)}
            </Text>

            {isAuthenticated ? (
              <Button variant="primary" size="lg" onPress={handleAccept}>
                <Text variant="body" weight="semibold" color={colors.background.primary}>
                  Accept Invitation
                </Text>
              </Button>
            ) : (
              <VStack spacing="md" style={styles.fullWidth}>
                <Button variant="primary" size="lg" onPress={handleLoginAndAccept}>
                  <Text variant="body" weight="semibold" color={colors.background.primary}>
                    Log in to Accept
                  </Text>
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onPress={() => router.push(`/signup?inviteToken=${token}`)}
                >
                  <Text variant="body" weight="semibold" color={colors.text.primary}>
                    Create Account
                  </Text>
                </Button>
              </VStack>
            )}
          </VStack>
        )}

        {status === 'accepting' && (
          <VStack spacing="lg" style={styles.center}>
            <ActivityIndicator size="large" color={colors.primary[500]} />
            <Text variant="body" color={colors.text.secondary}>
              Joining organization...
            </Text>
          </VStack>
        )}

        {status === 'accepted' && (
          <VStack spacing="lg" style={styles.center}>
            <CheckCircle size={48} color={colors.status.success.main} />
            <Text variant="h5" color={colors.text.primary} style={styles.textCenter}>
              Welcome to {inviteInfo?.organizationName}!
            </Text>
            <Text variant="body" color={colors.text.secondary} style={styles.textCenter}>
              Redirecting to dashboard...
            </Text>
          </VStack>
        )}

        {status === 'expired' && (
          <VStack spacing="lg" style={styles.center}>
            <Clock size={48} color={colors.status.warning.main} />
            <Text variant="h5" color={colors.text.primary} style={styles.textCenter}>
              Invitation Expired
            </Text>
            <Text variant="body" color={colors.text.secondary} style={styles.textCenter}>
              This invitation has expired. Please ask the team admin to send a new one.
            </Text>
            <Button variant="secondary" size="md" onPress={() => router.replace('/login')}>
              <Text variant="body" weight="medium" color={colors.text.primary}>
                Go to Login
              </Text>
            </Button>
          </VStack>
        )}

        {status === 'invalid' && (
          <VStack spacing="lg" style={styles.center}>
            <XCircle size={48} color={colors.status.error.main} />
            <Text variant="h5" color={colors.text.primary} style={styles.textCenter}>
              Invalid Invitation
            </Text>
            <Text variant="body" color={colors.text.secondary} style={styles.textCenter}>
              This invitation link is invalid or has already been used.
            </Text>
            <Button variant="secondary" size="md" onPress={() => router.replace('/login')}>
              <Text variant="body" weight="medium" color={colors.text.primary}>
                Go to Login
              </Text>
            </Button>
          </VStack>
        )}

        {status === 'error' && (
          <VStack spacing="lg" style={styles.center}>
            <XCircle size={48} color={colors.status.error.main} />
            <Text variant="h5" color={colors.text.primary} style={styles.textCenter}>
              Something went wrong
            </Text>
            <Text variant="body" color={colors.text.secondary} style={styles.textCenter}>
              {errorMessage}
            </Text>
            <Button variant="primary" size="md" onPress={handleAccept}>
              <Text variant="body" weight="medium" color={colors.background.primary}>
                Try Again
              </Text>
            </Button>
          </VStack>
        )}
      </Box>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.lg,
  },
  card: {
    maxWidth: 440,
    width: '100%',
  },
  center: {
    alignItems: 'center',
  },
  textCenter: {
    textAlign: 'center',
  },
  fullWidth: {
    width: '100%',
  },
});
